import { DownloadOutlined } from '@ant-design/icons';
import { Button, Table, Tag, message } from 'antd';
import { downloadFile, errorMessage } from '../../api/client';
import { useInvestments, useProfitPayments, useWithdrawals } from '../../api/hooks';
import { date, money } from '../../utils/format';

interface ReceiptRow {
  key: string;
  date: string;
  type: 'Investment' | 'Withdrawal' | 'Profit Payment';
  reference: string;
  amount: number;
  url: string;
  fileName: string;
}

const TYPE_COLOR: Record<ReceiptRow['type'], string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

export default function ReceiptsTab({ investorId }: { investorId: number }) {
  const { data: investments, isLoading: loadingInvestments } = useInvestments(investorId);
  const { data: withdrawals, isLoading: loadingWithdrawals } = useWithdrawals(investorId);
  const { data: payments, isLoading: loadingPayments } = useProfitPayments(investorId);

  const rows: ReceiptRow[] = [
    ...(investments ?? []).map((i) => ({
      key: `inv-${i.id}`,
      date: i.investmentDate,
      type: 'Investment' as const,
      reference: `INV-R-${String(i.id).padStart(6, '0')}`,
      amount: i.amount,
      url: `/receipts/investment/${i.id}`,
      fileName: `investment_receipt_${i.id}.pdf`,
    })),
    ...(withdrawals ?? []).map((w) => ({
      key: `wd-${w.id}`,
      date: w.withdrawalDate,
      type: 'Withdrawal' as const,
      reference: `WD-R-${String(w.id).padStart(6, '0')}`,
      amount: w.amount,
      url: `/receipts/withdrawal/${w.id}`,
      fileName: `withdrawal_receipt_${w.id}.pdf`,
    })),
    ...(payments ?? []).map((p) => ({
      key: `pp-${p.id}`,
      date: p.paymentDate,
      type: 'Profit Payment' as const,
      reference: p.receiptNo ?? `PP-R-${String(p.id).padStart(6, '0')}`,
      amount: p.amount,
      url: `/receipts/profit-payment/${p.id}`,
      fileName: `profit_payment_receipt_${p.id}.pdf`,
    })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Table<ReceiptRow>
      rowKey="key"
      loading={loadingInvestments || loadingWithdrawals || loadingPayments}
      dataSource={rows}
      scroll={{ x: 'max-content' }}
      columns={[
        { title: 'Date', dataIndex: 'date', render: date },
        {
          title: 'Type',
          dataIndex: 'type',
          render: (value: ReceiptRow['type']) => <Tag color={TYPE_COLOR[value]}>{value}</Tag>,
        },
        { title: 'Receipt no', dataIndex: 'reference' },
        { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
        {
          title: 'Download',
          render: (_, row) => (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadFile(row.url, row.fileName).catch((err) => message.error(errorMessage(err)))}
            >
              PDF
            </Button>
          ),
        },
      ]}
    />
  );
}
